import debounce from 'lodash.debounce';
import React, {
    useContext,
    useEffect,
    useRef,
    useState,
    useCallback,
} from 'react';

import { Search } from '@zooplus/icons';

import AppContext from '../../context';
import {
    dispatchSearchSuggestionStatusEvent,
    SearchSuggestionToggleEnum,
} from '../../tracking';
import { ThemedIcon } from '../Icon/Icon';
import SearchSuggestion from '../SearchSuggestion';

import { useSearchEngine } from './engines';
import { UiSuggestions } from './engines/types';
import styles from './SearchBar.module.scss';

const SEARCH_BAR_ID = 'search_query_field';
const DEBOUNCE_TIME = 300;
const SUGGESTIONS_ID = 'srh_suggestions';
export const SEARCH_MIDDLE_BAR_BUTTON_ID = 'srh-mbsearch';

interface BodyStyleProperty {
    width: string;
    position: string;
}

export default function SearchBar({
    device,
}: {
    device: 'mobile' | 'desktop';
}): JSX.Element {
    const context = useContext(AppContext);
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState<UiSuggestions | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [liveMessage, setLiveMessage] = useState('');
    const searchInput = useRef<HTMLInputElement>(null);
    const firstRender = useRef(true);
    const initialBodyStyleProperty = useRef<BodyStyleProperty | null>(null);

    const { translations } = context;

    function getSearchTermFromQueryMaybe() {
        const param = new URLSearchParams(window.location.search);
        return param.get('q') ?? '';
    }

    function setBodyStyleProperty(width: string, position: string) {
        if (document && document.body) {
            document.body.style.width = width;
            document.body.style.position = position;
        }
    }

    function disableBodyScroll() {
        /* Require below code to ensure on suggestion panel activation background does not scroll, 
        need property fixed to avoid race condition with burger navigation panel open with overflow hidden */

        if (!initialBodyStyleProperty.current && document.body && document) {
            initialBodyStyleProperty.current = {
                width: `${document.body.style.width}`,
                position: `${document.body.style.position}`,
            };
        }

        if (
            initialBodyStyleProperty.current &&
            document &&
            document.body &&
            device === 'mobile'
        ) {
            const isSuggestionPanelOpen =
                suggestions !== null &&
                suggestions.suggestionItems.length > 0 &&
                showSuggestions;
            const bodyWidth = isSuggestionPanelOpen
                ? '100%'
                : initialBodyStyleProperty.current.width;
            const bodyPosition = isSuggestionPanelOpen
                ? 'fixed'
                : initialBodyStyleProperty.current.position;

            setBodyStyleProperty(bodyWidth, bodyPosition);
        }
    }

    useEffect(() => {
        if (suggestions !== null) {
            dispatchSearchSuggestionStatusEvent({
                status: showSuggestions
                    ? SearchSuggestionToggleEnum.SHOWN
                    : SearchSuggestionToggleEnum.HIDDEN,
            });
        }

        disableBodyScroll();
    }, [suggestions, showSuggestions]);

    useEffect(() => {
        setSearchTerm(getSearchTermFromQueryMaybe());
        firstRender.current = false;

        return () => {
            if (document && document.body && initialBodyStyleProperty.current) {
                const {
                    current: { width, position },
                } = initialBodyStyleProperty;
                setBodyStyleProperty(width, position);
            }
        };
    }, []);

    // For VoiceOver Accessibility
    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout>;
        if (suggestions && suggestions.suggestionItems.length) {
            const liveMessageLabel = translations.get(
                'shop.header.suggestion.results.aria.label',
            );
            timeoutId = setTimeout(() => {
                if (liveMessageLabel) {
                    setLiveMessage(
                        liveMessageLabel.replace('{0}', `${searchTerm}`),
                    );
                }
            }, 1500);
        }
        return () => clearTimeout(timeoutId);
    }, [searchTerm, suggestions?.suggestionItems?.length]);

    const { redirectToSearchResults, fetchSuggestions, redirectToServicePage } =
        useSearchEngine();

    let timeOutId: number;

    function onBlur() {
        timeOutId = window.setTimeout(() => {
            setShowSuggestions(false);
        }, 100);
    }

    function onFocus() {
        clearTimeout(timeOutId);
        setShowSuggestions(true);
    }

    async function apiRequest(term: string) {
        const suggestionsData = await fetchSuggestions(term);
        setQuery(term);
        if (suggestionsData) {
            setSuggestions(suggestionsData);
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }
        return suggestionsData;
    }

    async function onSubmitSearch(event: React.FormEvent) {
        event.preventDefault();
        let suggestionsData = suggestions;
        if (searchTerm !== query) {
            suggestionsData = await apiRequest(searchTerm);
        }
        if (
            suggestionsData?.redirectUri !== null &&
            suggestionsData?.redirectUri !== undefined
        ) {
            redirectToServicePage(suggestionsData.redirectUri);
        } else {
            redirectToSearchResults(searchTerm);
        }
    }

    const debouncedAutoComplete = useCallback(
        debounce(apiRequest, DEBOUNCE_TIME),
        [context.selectedShopLanguage],
    );

    function handleSearchOnChange(event: React.ChangeEvent<HTMLInputElement>) {
        event.persist();
        const {
            target: { value },
        } = event;
        setSearchTerm(value);
        if (value.length > 2) {
            debouncedAutoComplete(value);
        } else {
            setSuggestions(null);
        }
    }

    function handleKeyUp(e: React.KeyboardEvent) {
        if (e.key === 'Enter') {
            setShowSuggestions(!showSuggestions);
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
        }
    }
    return (
        <search
            role="presentation"
            className={styles.searchBarWrapper}
            onFocus={() => onFocus()}
            onBlur={() => onBlur()}
            onKeyUp={(e: React.KeyboardEvent) => handleKeyUp(e)}>
            <form
                data-zta={`search_form_${device}`}
                className={styles.searchForm}
                name="miniSearchBox"
                onSubmit={onSubmitSearch}
                autoComplete="off">
                <label
                    htmlFor={`${SEARCH_BAR_ID}_${device}`}
                    className={styles.accessibleText}
                    id={`${SEARCH_BAR_ID}-label-${device}`}>
                    {translations.get('shop.header.search.label')}
                </label>
                <input
                    data-zta={`search_form_input_${device}`}
                    id={`${SEARCH_BAR_ID}_${device}`}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleSearchOnChange(e)
                    }
                    ref={searchInput}
                    value={searchTerm}
                    name="q"
                    type="search"
                    inputMode="search"
                    placeholder={translations.get(
                        'shop.header.search.placeholder',
                    )}
                    aria-labelledby={`${SEARCH_BAR_ID}-label-${device}`}
                    aria-describedby={`${SEARCH_BAR_ID}-description-${device}`}
                    className={styles.searchBarInput}
                />
                {/* Should be in the DOM to be accessed via VoiceOver */}
                {showSuggestions && (
                    <div
                        role="status"
                        aria-live="polite"
                        aria-atomic="true"
                        className={styles.srOnly}>
                        {liveMessage}
                    </div>
                )}

                {showSuggestions &&
                    suggestions &&
                    suggestions.suggestionItems &&
                    suggestions.suggestionItems.length > 0 && (
                        <SearchSuggestion
                            id={SUGGESTIONS_ID}
                            device={device}
                            term={query}
                            suggestions={suggestions.suggestionItems}
                        />
                    )}
                <button
                    className={styles.submitButton}
                    data-zta={`search_form_button_${device}`}
                    type="submit"
                    aria-labelledby={`${SEARCH_BAR_ID}-label-${device}`}>
                    <ThemedIcon
                        Icon={Search}
                        color="primary"
                        aria-labelledby={`${SEARCH_BAR_ID}-label-${device}`}
                    />
                </button>
            </form>
        </search>
    );
}
